import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  ClipboardList,
  Plus,
  Search,
  BarChart3,
  Users,
  MoreVertical,
  Edit,
  Trash2,
  Share2,
  Eye,
  LogOut,
  User,
  Settings,
  ChevronDown,
  Activity,
  FileText,
  LayoutGrid,
  Loader2,
  UserPlus,
  FileEdit,
  Download
} from 'lucide-react';
import { authAPI, surveysAPI } from '../services/api';
import { MobileNavAdmin } from '../components/MobileNav';

const SurveysPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }
    
    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      const surveysData = await surveysAPI.getAll();
      setSurveys(surveysData);
    } catch (error) {
      if (error.response?.status === 401) navigate('/auth');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await authAPI.logout();
    navigate('/auth');
  };

  const handleDeleteSurvey = async (id) => {
    if (!window.confirm('Are you sure you want to delete this survey?')) return;
    try {
      await surveysAPI.delete(id);
      setSurveys(surveys.filter(s => s.id !== id));
    } catch (error) {
      alert('Failed to delete survey');
    }
  };

  const handleExportReport = async (survey) => {
    try {
      const response = await surveysAPI.exportReport(survey.id);
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${survey.title}_report.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to export report');
    }
  };

  const filteredSurveys = surveys.filter(s =>
    s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTypeColor = (type) => {
    const colors = {
      kap: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      mrs: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      patient: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
      hcp: 'bg-amber-500/10 text-amber-400 border-amber-500/20'
    };
    return colors[type] || 'bg-slate-500/10 text-slate-400';
  };

  const getTypeName = (type) => {
    const names = { kap: 'KAP Survey', mrs: 'Market Research', patient: 'Patient Survey', hcp: 'HCP Feedback' };
    return names[type] || type;
  };

  const isAdmin = user?.role === 'administrator';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-slate-900 border-r border-slate-800 z-40 hidden lg:flex flex-col">
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/dashboard')}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">KAP<span className="text-cyan-400">Surv</span></span>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          <button onClick={() => navigate('/dashboard')} className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-colors">
            <LayoutGrid className="w-5 h-5" /> Dashboard
          </button>
          <button className="w-full flex items-center gap-3 px-4 py-3 text-white bg-white/10 rounded-xl font-medium">
            <ClipboardList className="w-5 h-5" /> Surveys
          </button>
          <button onClick={() => navigate('/analytics')} className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-colors">
            <BarChart3 className="w-5 h-5" /> Analytics
          </button>
          {isAdmin && (
            <button onClick={() => navigate('/users')} className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-colors">
              <UserPlus className="w-5 h-5" /> Manage Users
            </button>
          )}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-white font-semibold">
                  {user?.name?.charAt(0) || 'U'}
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                  <p className="text-xs text-slate-500 truncate">{isAdmin ? 'Administrator' : user?.role}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-slate-900 border-slate-800">
              <DropdownMenuItem className="gap-2 text-slate-300"><User className="w-4 h-4" /> Profile</DropdownMenuItem>
              <DropdownMenuItem className="gap-2 text-slate-300"><Settings className="w-4 h-4" /> Settings</DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-800" />
              <DropdownMenuItem className="gap-2 text-red-400" onClick={handleLogout}><LogOut className="w-4 h-4" /> Log out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64 p-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Surveys</h1>
            <p className="text-slate-400">Manage and build your survey questionnaires</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Search surveys..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-64 bg-slate-900 border-slate-800 text-white placeholder:text-slate-500 focus:border-cyan-500 rounded-xl"
              />
            </div>
            {isAdmin && (
              <Button 
                onClick={() => navigate('/dashboard?newSurvey=true')} 
                className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500"
              >
                <Plus className="w-4 h-4 mr-2" /> New Survey
              </Button>
            )}
          </div>
        </div>

        <div className="grid gap-4">
          {filteredSurveys.length === 0 ? (
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="py-12 text-center">
                <ClipboardList className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                <p className="text-slate-500">No surveys found. Create one from the dashboard.</p>
              </CardContent>
            </Card>
          ) : (
            filteredSurveys.map((survey) => (
              <Card key={survey.id} className="bg-slate-900 border-slate-800 hover:border-slate-700 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-white">{survey.title}</h3>
                        <Badge variant="outline" className={getTypeColor(survey.type)}>{getTypeName(survey.type)}</Badge>
                        {survey.status === 'active' && (
                          <span className="flex items-center gap-1 text-xs text-emerald-400">
                            <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" /> Live
                          </span>
                        )}
                        {survey.status === 'draft' && <Badge variant="outline" className="text-slate-400 border-slate-600">Draft</Badge>}
                      </div>
                      <p className="text-slate-400 mb-4">{survey.description}</p>
                      <div className="flex items-center gap-6 text-sm text-slate-500">
                        <span>{survey.responses} / {survey.target_responses} responses</span>
                        <span>{Math.round(survey.completion_rate)}% complete</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => navigate(`/surveys/${survey.id}/builder`)}
                        className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500 text-white gap-2 rounded-xl"
                      >
                        <FileEdit className="w-4 h-4" /> Build Survey
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-10 w-10 p-0 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl">
                            <MoreVertical className="w-5 h-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-slate-900 border-slate-800">
                          <DropdownMenuItem className="gap-2 text-slate-300 cursor-pointer" onClick={() => navigate(`/surveys/${survey.id}/respond`)}><Eye className="w-4 h-4" /> Preview</DropdownMenuItem>
                          <DropdownMenuItem className="gap-2 text-slate-300 cursor-pointer" onClick={() => navigate(`/surveys/${survey.id}/analytics`)}><BarChart3 className="w-4 h-4" /> Analytics</DropdownMenuItem>
                          <DropdownMenuItem className="gap-2 text-slate-300 cursor-pointer" onClick={() => handleExportReport(survey)}><Download className="w-4 h-4" /> Export Report</DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-slate-800" />
                          <DropdownMenuItem className="gap-2 text-red-400 cursor-pointer" onClick={() => handleDeleteSurvey(survey.id)}><Trash2 className="w-4 h-4" /> Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileNavAdmin />
      
      {/* Bottom Padding for Mobile Nav */}
      <div className="lg:hidden h-24" />
    </div>
  );
};

export default SurveysPage;
